import { getVisibleSocialProviders, useSocialList } from "@/components/socialLogin/helper.ts";
import i18n from "@/i18n.ts";
import { apiConfig, promotionConfig } from "@/lib/env";
import { isSupportedLocale } from "@/lib/i18n/config";
import { toUrlSearchParams } from "@/utils/urlSearchParams";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { useCallback, useMemo } from "react";

type SocialProvider = {
  name: string;
  online_url: string;
  name_key: string;
  client_id?: unknown;
};

const HEADER_SOCIAL_PROVIDER_KEYS = new Set(["google", "telegram"]);

export default function GoogleAuth({ enabled }: { enabled?: boolean }) {
  const locationSearchParams = useSearchParams();
  const search = locationSearchParams.toString() ? `?${locationSearchParams.toString()}` : "";
  const locale = useLocale();
  const redirectLocale = isSupportedLocale(locale) ? locale : "en";
  const redirectUri = typeof window === "undefined" ? "" : `${window.location.origin}/${redirectLocale}`;

  // 当前站支持的社媒
  const { data: social } = useSocialList(enabled);
  const visibleSocialProviders = useMemo(
    () => getVisibleSocialProviders<SocialProvider>(social?.data),
    [social?.data]
  );

  const params = useMemo(() => {
    let baseParams: Record<string, any> = {
      locale: i18n.language || "en",
      service: apiConfig.url?.replace("/api", ""),
      redirect_uri: redirectUri,
    };

    const model = promotionConfig.model;
    const appid = promotionConfig.folder;

    const startapp = toUrlSearchParams(search).get("startapp");

    // For RoiBest
    if (appid && model === "roibest") {
      baseParams = { ...baseParams, appid };
    }

    // For Referral Link
    if (startapp) {
      baseParams = { ...baseParams, startapp };
    }

    return baseParams;
  }, [redirectUri, search, i18n.language]);

  // social login website
  const getAuthLink = useCallback(
    (url: string, auth_type: string) => {
      const searchParams = new URLSearchParams({ ...params, auth_type });
      window.location.href = `${url}?${searchParams}`;
    },
    [params],
  );

  return (
    <>
      {visibleSocialProviders.length > 0 &&
        visibleSocialProviders.map((s: SocialProvider) => {
          if (!HEADER_SOCIAL_PROVIDER_KEYS.has(s?.name_key)) return null;

          return <SocialButton key={s.name_key} name={s.name} nameKey={s.name_key} onClick={() => getAuthLink(s.online_url, s.name_key)} />;
        })}
    </>
  );
}

const SocialButton = ({ name, nameKey, ...props }: React.ComponentProps<"button"> & { name: string; nameKey: string }) => {
  return (
    <button {...props} className="btn btn-primary btn-soft btn-square text-sm h-9 w-9">
      <img src={`/images/social/${nameKey}.svg`} className="h-6 w-6" alt={name} />
    </button>
  );
};
